import { useQuery } from '@tanstack/react-query'
import { ClipboardPen } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { Alert } from '@/components/ui/Alert'
import { Card } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { FormField } from '@/components/forms/FormField'
import { papersQuery } from '../api'
import type { Exam } from '../types'
import { MarkSheetPanel } from './MarkSheetPanel'

/** Admin mark entry: choose class → section → paper, then edit the sheet. */
export function MarkEntryPanel({ exam }: { exam: Exam }) {
  const { t } = useTranslation('exams')
  const papers = useQuery(papersQuery(exam.id))
  const [classId, setClassId] = useState<number>(exam.classes[0]?.id ?? 0)
  const cls = exam.classes.find((c) => c.id === classId)
  const [sectionId, setSectionId] = useState<number>(cls?.sections[0]?.id ?? 0)
  const classPapers = (papers.data ?? []).filter((p) => p.classId === classId)
  const [paperId, setPaperId] = useState<number>(0)
  const effectivePaper = classPapers.some((p) => p.id === paperId) ? paperId : (classPapers[0]?.id ?? 0)

  if (papers.isPending)
    return (
      <Card className="space-y-3 p-5">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </Card>
    )
  if (papers.error)
    return (
      <Card>
        <ErrorState onRetry={() => void papers.refetch()} />
      </Card>
    )
  if (papers.data.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<ClipboardPen />}
          title={t('No papers to grade')}
          description={t('Add papers to this exam first.')}
        />
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {exam.status === 'draft' || exam.status === 'scheduled' ? (
        <Alert tone="info" title={t('Marks entry is not open yet')}>
          {t('Open marks entry from the header to let teachers grade their papers.')}
        </Alert>
      ) : null}
      <Card className="grid gap-4 p-4 sm:grid-cols-3">
        <FormField label={t('Class')}>
          <Select
            value={classId}
            onChange={(e) => {
              const id = Number(e.target.value)
              setClassId(id)
              setSectionId(exam.classes.find((c) => c.id === id)?.sections[0]?.id ?? 0)
            }}
            options={exam.classes.map((c) => ({ value: c.id, label: c.name }))}
          />
        </FormField>
        <FormField label={t('Section')}>
          <Select
            value={sectionId}
            onChange={(e) => setSectionId(Number(e.target.value))}
            options={(cls?.sections ?? []).map((s) => ({ value: s.id, label: s.name || t('Whole class') }))}
          />
        </FormField>
        <FormField label={t('Paper')}>
          <Select
            value={effectivePaper}
            onChange={(e) => setPaperId(Number(e.target.value))}
            options={classPapers.map((p) => ({ value: p.id, label: p.subjectName }))}
          />
        </FormField>
      </Card>
      {effectivePaper && sectionId ? (
        <MarkSheetPanel
          key={`${effectivePaper}-${sectionId}`}
          examId={exam.id}
          paperId={effectivePaper}
          sectionId={sectionId}
        />
      ) : (
        <Card>
          <EmptyState
            title={t('Nothing to show')}
            description={t('This class has no sections or papers.')}
            compact
          />
        </Card>
      )}
    </div>
  )
}
